import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { USER_LINKS_ENDPOINT } from 'src/app/constants/api-routes';
import { UserLinkRecord, userLinksResponseToRecords } from 'src/app/model/user-link';
import { UserLinksStore } from 'src/app/stores/user-links.store';
import { UserService } from '../user/user.service';

export type UserLinksApiResponse = Record<string, string | undefined>;

@Injectable({
  providedIn: 'root',
})
export class UserLinksService {
  private readonly _http = inject(HttpClient);
  private readonly _userService = inject(UserService);
  private readonly _userLinksStore = inject(UserLinksStore);

  /**
   * Fetches user links for the active company, updates {@link UserLinksStore}, and emits the mapped records.
   */
  fetchUserLinks(): Observable<UserLinkRecord[]> {
    const companyId = this._userService.company?.companyId;
    if (companyId == null) {
      return throwError(() => new Error('Company id is missing'));
    }

    this._userLinksStore.setLoading();

    return this._http.get<UserLinksApiResponse>(USER_LINKS_ENDPOINT(companyId)).pipe(
      map((body) => userLinksResponseToRecords(body)),
      tap((records) => {
        this._userLinksStore.setRecords(records);
      }),
      catchError((err: unknown) => {
        const message = this.resolveErrorMessage(err);
        this._userLinksStore.setError(message);
        return throwError(() => err);
      }),
    );
  }

  private resolveErrorMessage(err: unknown): string {
    if (err && typeof err === 'object' && 'error' in err) {
      const body = (err as { error?: { message?: string } | string }).error;
      if (typeof body === 'string' && body.trim()) {
        return body;
      }
      if (body && typeof body === 'object' && 'message' in body) {
        const m = (body as { message?: string }).message;
        if (typeof m === 'string' && m.trim()) {
          return m;
        }
      }
    }
    return 'Unable to load links';
  }
}
